/* eslint-disable complexity */
import _ from 'lodash';
import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components/macro';

import MUIDialog, { DialogProps as MUIDialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

import { useTranslation } from 'ev-i18n';

import Button, {
  ButtonProps,
  ButtonSize,
  ButtonTypes,
} from 'ev-components/Button';
import Icon from 'ev-components/Icon';
import Text from 'ev-components/Text';
import useLayout from 'ev-hooks/layout';
import { EVColors } from 'ev-theme/styles';

type TextAlignment = 'left' | 'center';

export type DialogProps = {
  id?: string;
  className?: string;
  children?: React.ReactNode;
  open: boolean;
  title?: string;
  titleColor?: string;
  icon?: IconDefinition;
  iconColor?: string;
  textAlign?: TextAlignment;
  onAccept?: MouseEventHandler | (() => void);
  onReject?: () => void;
  onClose?: () => void;
  acceptText?: string;
  rejectText?: string;
  acceptId?: string;
  rejectId?: string;
  banner?: React.ReactNode;
  renderCustomizedContent?: React.ReactNode;
  width?: number | string;
  busy?: boolean;
  required?: boolean;
  hideReject?: boolean;
  hideAccept?: boolean;
  centerActions?: boolean;
  container?: Element | (() => Element | null) | null | undefined;
  sx?: { [key: string]: string | number | React.CSSProperties };
  headerMargin?: string;
  rejectButtonProps?: Partial<ButtonProps>;
  acceptButtonProps?: Partial<ButtonProps>;
  customAction?: () => void;
  scroll?: MUIDialogProps['scroll'];
  scrollShadow?: boolean;
  hideCloseButton?: boolean;
  closeAfter?: number;
  divider?: boolean;
  paddingBottom?: string;
  borderBottom?: string;
};

const Dialog = ({
  id,
  open,
  children,
  title,
  icon,
  iconColor,
  textAlign = 'center',
  onAccept = _.noop,
  onReject = _.noop,
  onClose = _.noop,
  acceptText,
  acceptId = 'dialog-primary',
  rejectText,
  rejectId = 'dialog-secondary',
  banner,
  width,
  busy,
  required,
  hideReject,
  hideAccept,
  centerActions,
  className = 'dialog',
  container = document.body,
  headerMargin,
  sx = {},
  rejectButtonProps,
  acceptButtonProps,
  customAction,
  scroll = 'body',
  scrollShadow,
  hideCloseButton = false,
  closeAfter,
  renderCustomizedContent,
  divider,
  paddingBottom,
  borderBottom,
}: DialogProps) => {
  const { t } = useTranslation();
  const { isMobile } = useLayout();
  const [isModalVisible, setIsModalVisible] = useState(open);
  const [disableCancelButton, setDisableCancelButton] = useState(false);
  const [disableOkButton, setDisableOkButton] = useState(false);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      setDisableOkButton(false);
    }
  }, [open]);

  useEffect(() => {
    if (busy) {
      setDisableCancelButton(true);
    } else {
      setDisableCancelButton(false);
    }
  }, [busy]);

  useEffect(() => {
    if (open && closeAfter) {
      setTimer(
        setTimeout(() => {
          onClose();
        }, closeAfter),
      );
    }
  }, [open, closeAfter, onClose]);

  useEffect(() => {
    setIsModalVisible(open);
  }, [open]);

  useEffect(() => {
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [timer]);

  const handleClose = () => {
    if (required) {
      return;
    }
    onClose();
    setDisableOkButton(false);
    if (timer) {
      clearTimeout(timer);
    }
  };

  const handleCancelClick = useCallback(() => {
    setDisableOkButton(true);
    onReject();
    if (cancelButtonRef.current) {
      cancelButtonRef.current.focus();
    }
  }, [onReject]);

  const renderActions = () => {
    const buttonSize = isMobile ? ButtonSize.Large : ButtonSize.Medium;
    const buttonVariant = isMobile
      ? ButtonTypes.Secondary
      : ButtonTypes.Special;

    return (
      (!hideReject || !hideAccept) && (
        <DialogActions
          sx={{
            justifyContent:
              banner || centerActions || isMobile ? 'center' : 'flex-end',
            boxShadow: scrollShadow
              ? '0px 2px 17px -4px rgba(0, 0, 0, 0.08)'
              : undefined,
          }}
        >
          {customAction?.()}
          {!hideReject && (
            <Button
              disabled={disableCancelButton}
              fullWidth={isMobile}
              id={rejectId}
              onClick={handleCancelClick}
              ref={cancelButtonRef}
              size={buttonSize}
              variant={buttonVariant}
              {...rejectButtonProps}
            >
              {rejectText || t('Cancel')}
            </Button>
          )}
          {!hideAccept && (
            <Button
              busy={busy}
              disabled={acceptButtonProps?.disabled || busy || disableOkButton}
              fullWidth={isMobile}
              id={acceptId}
              onClick={onAccept}
              size={buttonSize}
              variant={ButtonTypes.Primary}
              {...acceptButtonProps}
            >
              {acceptText || t('Ok')}
            </Button>
          )}
        </DialogActions>
      )
    );
  };

  const showCloseButton = () => !hideCloseButton && !banner;

  const labelId = title && 'dialog-title';

  return (
    <MUIDialog
      PaperProps={{
        sx: {
          width: width ?? (isMobile ? 'calc(100vw - 48px)' : 560),
          margin: '24px',
          '& .MuiDialogActions-root': {
            paddingTop: isMobile ? '8px' : '10px',
          },
          borderRadius: '8px',
        },
      }}
      aria-labelledby={labelId}
      className={className}
      container={container}
      data-testid={id}
      id={id}
      onClose={handleClose}
      open={isModalVisible}
      scroll={scroll}
      sx={sx}
    >
      {renderCustomizedContent ? (
        renderCustomizedContent
      ) : (
        <>
          {banner}
          <Stack
            alignItems="flex-start"
            className="dialogHeader"
            direction="row"
            margin={headerMargin}
            sx={{
              pt: '24px',
              px: '24px',
              pb: paddingBottom || '9px',
              borderBottom,
            }}
          >
            {(icon || title) && (
              <Stack
                alignItems="center"
                direction="row"
                flex={1}
                justifyContent={
                  textAlign === 'center' ? 'center' : 'flex-start'
                }
              >
                {icon && (
                  <StyledIcon
                    $textAlign={textAlign}
                    color={iconColor}
                    fixedWidth={false}
                    icon={icon}
                  />
                )}
                {title && (
                  <Text.Title
                    color={EVColors.asphalt}
                    id={labelId}
                    paddingLeft={
                      icon
                        ? '8px'
                        : textAlign === 'center' && showCloseButton()
                          ? '34px'
                          : '0px'
                    }
                    sx={{
                      display: 'block',
                      textAlign,
                    }}
                    bold
                  >
                    {title}
                  </Text.Title>
                )}
              </Stack>
            )}
            {showCloseButton() && (
              <CloseButton
                ariaLabel={t('Close Dialog')}
                id="dialog-close"
                onClick={onClose}
                variant={ButtonTypes.Tertiary}
              >
                <Icon color={EVColors.vulcan} icon={faTimes} />
              </CloseButton>
            )}
          </Stack>

          {divider && <Divider />}
          {children && (
            <Content $align={textAlign}>
              <Text.Description color={EVColors.mako}>
                {children}
              </Text.Description>
            </Content>
          )}
          {renderActions()}
        </>
      )}
    </MUIDialog>
  );
};

export default Dialog;

const Content = styled(DialogContent)<{
  $align: TextAlignment;
}>`
  text-align: ${p => p.$align};
  min-height: 32px;
`;

const CloseButton = styled(Button)`
  min-width: 0;
  padding: 8px;
  height: 25px;
  left: 5px;
  margin-left: auto;
`;

const StyledIcon = styled(Icon)<{ $textAlign: TextAlignment }>`
  height: 24px;
  width: 24px;
  padding-left: ${p => (p.$textAlign === 'center' ? '34px' : '0px')};
`;
